// Public homepage i18n — shares the IFI_LOCALE localStorage key with
// src/admin/i18n.ts so a single user preference covers both surfaces.
//
// Scope cap: only priority surfaces (topbar, section titles, ranking
// controls, Supabase status, modal tabs, About/Story headers, academic
// foundation). Dense body text remains Indonesian — Phase I backlog.

export type Locale = 'id' | 'en';

const STORAGE_KEY = 'IFI_LOCALE';

export function getLocale(): Locale {
  try {
    return localStorage.getItem(STORAGE_KEY) === 'en' ? 'en' : 'id';
  } catch {
    return 'id';
  }
}

export function setLocale(loc: Locale): void {
  try {
    localStorage.setItem(STORAGE_KEY, loc);
  } catch {
    /* noop */
  }
}

export type Dict = Record<string, { id: string; en: string }>;

export function applyI18n(dict: Dict, root: Document | HTMLElement = document): void {
  const loc = getLocale();
  root.querySelectorAll<HTMLElement>('[data-i18n-key]').forEach((el) => {
    const key = el.dataset.i18nKey;
    if (!key) return;
    const entry = dict[key];
    if (!entry) return;
    el.textContent = entry[loc];
  });
  // Page lang attribute follows the toggle so screen readers switch voice.
  document.documentElement.lang = loc;
}

export function wireLangToggle(dict: Dict, hostId = 'langToggle'): void {
  const host = document.getElementById(hostId);
  if (!host) return;
  const loc = getLocale();
  host.querySelectorAll<HTMLButtonElement>('[data-lang]').forEach((btn) => {
    if (btn.dataset.lang === loc) btn.classList.add('lang-toggle__btn--active');
    btn.addEventListener('click', () => {
      const next = (btn.dataset.lang as Locale) || 'id';
      if (next === loc) return;
      setLocale(next);
      // Reload so admin/i18n.ts and any cached DOM also re-render with the
      // new locale. Public dashboard has no session to lose.
      location.reload();
    });
  });
  applyI18n(dict);
}

// Public dictionary — priority surfaces only. Add keys here as new
// translatable elements are tagged with data-i18n-key in index.html.
export const PUBLIC_DICT: Dict = {
  'topbar.subtitle': {
    id: 'Deteksi dini risiko Investment-in-Failure · APBN sanitasi 2015-2024',
    en: 'Early detection of Investment-in-Failure risk · APBN sanitation 2015-2024',
  },
  'topbar.mode': { id: 'Mode', en: 'Mode' },
  'topbar.story': { id: 'Mode Naratif', en: 'Story Mode' },
  'topbar.about': { id: 'Tentang', en: 'About' },
  'topbar.studio': { id: '🛠 Studio', en: '🛠 Studio' },
  'mode.single': { id: 'Tahun Tunggal', en: 'Single Year' },
  'mode.range': { id: 'Rentang Tahun', en: 'Year Range' },
  'mode.compare': { id: 'Bandingkan Tahun', en: 'Compare Years' },
  'mode.compare_periods': { id: 'Bandingkan Periode', en: 'Compare Periods' },
  'mode.trajectory': { id: 'Trayektori', en: 'Trajectory' },
  'agg.latest': { id: 'Terbaru dalam rentang', en: 'Latest in range' },
  'agg.mean': { id: 'Rata-rata', en: 'Mean (average)' },
  'agg.min': { id: 'Minimum (terburuk)', en: 'Min (worst)' },
  'agg.max': { id: 'Maksimum (terbaik)', en: 'Max (best)' },
  'agg.worst_ever': { id: 'Status terburuk yang pernah ada', en: 'Worst-ever status' },
  'sidebar.filter': { id: 'Filter', en: 'Filters' },
  'chart.matrix.title': { id: 'Matriks Diagnostik 2×2 INFRAIMPACT', en: 'INFRAIMPACT 2×2 Diagnostic Matrix' },
  'chart.trend.title': { id: 'Tren 2015–2024', en: 'Trend 2015–2024' },
  'chart.ranking.title': { id: 'Ranking — Aset Idle/Failed per Provinsi', en: 'Ranking — Idle/Failed Assets per Province' },
  'tab.overview': { id: 'Ringkasan', en: 'Overview' },
  'tab.indicators': { id: 'Indikator', en: 'Indicators' },
  'tab.trajectory': { id: 'Trayektori', en: 'Trajectory' },
  'tab.recommendations': { id: 'Rekomendasi', en: 'Recommendations' },
  'tab.quality': { id: 'Kualitas Data', en: 'Data Quality' },
  'story.title': { id: 'Mode Naratif — INFRAIMPACT', en: 'Story Mode — INFRAIMPACT' },
  'about.title': { id: 'Tentang INFRAIMPACT', en: 'About INFRAIMPACT' },
  'supa.diag.summary': { id: 'Detail sumber data', en: 'Data source details' },
  'dataSource.live':    { id: 'Live DB',         en: 'Live DB' },
  'dataSource.syncing': { id: 'Sinkronisasi…',  en: 'Syncing…' },
  'dataSource.cached':  { id: 'Cache',           en: 'Cached' },
  'dataSource.json':    { id: 'Fallback',        en: 'Fallback' },
  'dataSource.mixed':   { id: 'Live + Fallback', en: 'Live + Fallback' },
  'dataSource.issue':   { id: 'Masalah data',    en: 'Data issue' },
  'dataSource.lastSync':{ id: 'Sync terakhir',   en: 'Last sync' },
  'dataSource.refresh': { id: 'Muat ulang data (Live DB)', en: 'Reload data (Live DB)' },
  'dataSource.reasonFallback': {
    id: 'Server tidak merespons. Menampilkan data cadangan terkurasi.',
    en: "Server didn't respond. Showing curated fallback dataset.",
  },
  'dataSource.detailsToggle': { id: 'Detail per-tabel', en: 'Per-table details' },
  'academic.title': { id: 'Fondasi Akademik INFRAIMPACT', en: 'Academic Foundation of INFRAIMPACT' },
  'academic.eyebrow': { id: 'Fondasi akademik', en: 'Academic foundation' },
  'academic.intro': {
    id: 'INFRAIMPACT berakar pada literatur ex-post evaluation, fiscal decentralization, institutional voids dan agency problem, asset management dan sanitation lifecycle, serta BWM dan composite indicator. Status pra-lapangan: kerangka diagnostik, bukan klaim dampak empiris final.',
    en: 'INFRAIMPACT draws on literature in ex-post evaluation, fiscal decentralization, institutional voids and agency problems, asset management and sanitation lifecycle, and BWM/composite indicator methods. Pre-fieldwork status: a diagnostic framework, not a final empirical impact claim.',
  },
  'academic.intro_compact': {
    id: 'Berakar pada literatur ex-post evaluation, fiscal decentralization, institutional voids, asset lifecycle, dan BWM/composite indicator.',
    en: 'Rooted in literature on ex-post evaluation, fiscal decentralization, institutional voids, asset lifecycle, and BWM/composite indicators.',
  },
  'academic.curated_note': {
    id: 'Daftar pustaka terkurasi berisi sumber yang telah digunakan dalam naskah tesis dan/atau mendukung pengembangan kerangka INFRAIMPACT.',
    en: 'The curated reference list contains sources used in the thesis manuscript and/or supporting the development of the INFRAIMPACT framework.',
  },
  'academic.openStudio': { id: 'Buka Literature Review di Studio →', en: 'Open Literature Review in Studio →' },
  'academic.refs_count': { id: 'referensi', en: 'references' },
  'academic.viewFoundation': { id: 'Lihat Fondasi Akademik', en: 'View Academic Foundation' },
  'academic.hideFoundation': { id: 'Sembunyikan Fondasi Akademik', en: 'Hide Academic Foundation' },
  'academic.key_reference':  { id: 'Referensi kunci',         en: 'Key reference' },
  'academic.more_in_studio': { id: 'lainnya di Studio',       en: 'more in Studio' },

  // Map activation state machine (Phase N).
  'map.title': { id: 'Peta interaktif Indonesia', en: 'Interactive map of Indonesia' },
  'map.activate': { id: '▶ Aktifkan peta interaktif', en: '▶ Activate interactive map' },
  'map.loading': { id: 'Memuat peta interaktif…', en: 'Loading interactive map…' },
  'map.loadingHint': {
    id: 'Peta dimuat terpisah agar halaman awal tetap cepat.',
    en: 'Map loads separately to keep the initial page fast.',
  },
  'map.error': { id: 'Peta gagal dimuat. Coba lagi.', en: 'Map failed to load. Try again.' },
  'map.retry': { id: 'Coba lagi', en: 'Try again' },

  // Thesis context strip (Phase J — moved below the main visualisations).
  'thesis.eyebrow.metadata': { id: 'Metadata tesis', en: 'Thesis metadata' },
  'thesis.title': {
    id: 'Kerangka Diagnostik Multidimensi INFRAIMPACT',
    en: 'INFRAIMPACT Multidimensional Diagnostic Framework',
  },
  'thesis.tagline': {
    id: 'Pengembangan kerangka diagnostik untuk deteksi dini risiko Investment-in-Failure pada infrastruktur sanitasi APBN Indonesia 2015–2024.',
    en: 'Development of a diagnostic framework for early detection of Investment-in-Failure risk in Indonesia’s state-funded sanitation infrastructure, 2015–2024.',
  },
  'thesis.chip.prelapangan': { id: 'PRA-LAPANGAN', en: 'PRE-FIELDWORK' },
  'thesis.chip.qualquan':   { id: 'QUAL → QUAN', en: 'QUAL → QUAN' },
  'thesis.chip.poc':        { id: 'Proof-of-concept', en: 'Proof of concept' },

  'thesis.eyebrow.rq': { id: 'Pertanyaan penelitian', en: 'Research questions' },
  'thesis.rq.diagnostic.label': { id: 'Diagnostic:', en: 'Diagnostic:' },
  'thesis.rq.diagnostic.body': {
    id: 'pisahkan akar masalah CAUSE dari dampak operasional IMPACT.',
    en: 'distinguish CAUSE-side root conditions from IMPACT-side operational outcomes.',
  },
  'thesis.rq.formative.label': { id: 'Formative:', en: 'Formative:' },
  'thesis.rq.formative.body': {
    id: 'bangun framework lintas institusi, regulasi, fiskal, teknis.',
    en: 'develop a cross-institutional, regulatory, fiscal, and technical framework.',
  },
  'thesis.rq.poc.label': { id: 'Proof-of-concept:', en: 'Proof of concept:' },
  'thesis.rq.poc.body': {
    id: 'uji koherensi awal pada kasus optimal, median, severe.',
    en: 'test initial coherence across optimal, median, and severe cases.',
  },

  'thesis.eyebrow.framework': { id: 'CAUSE / IMPACT / BRIDGE', en: 'CAUSE / IMPACT / BRIDGE' },
  'thesis.framework.cause': {
    id: 'Origin-stage: kesiapan institusi, regulasi, fiskal/O&M, permintaan awal.',
    en: 'Origin stage: institutional readiness, regulation, fiscal/O&M capacity, initial demand.',
  },
  'thesis.framework.impact': {
    id: 'Runtime-stage: fungsi teknis, kontinuitas layanan, utilisasi, compliance.',
    en: 'Runtime stage: technical function, service continuity, utilisation, compliance.',
  },
  'thesis.framework.bridge': {
    id: 'BAST, data UPTD/BLUD, kapasitas desain vs realisasi.',
    en: 'BAST handover, UPTD/BLUD operator data, design vs realised capacity.',
  },
};
